/**
 * Authentication Sagas
 */
/* tslint:disable:space-before-function-paren */

import { clearLocalStorage } from '../actions/localStorage';
import * as actions from '../actions/user';
import { getUserPool } from '../helpers/authentication';
import {
  authenticateWithToken,
  logInUser,
  logOutUser,
  resetPassword,
  sendConfirmationEmail,
  signUpUser,
  updateProfile,
  verifyUserEmail,
} from '../helpers/user';

import { CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { effects as fx, SagaIterator } from 'redux-saga';
import { PayloadError } from 'relay-runtime';
import { Action, AsyncActionCreators } from 'typescript-fsa';
import { bindAsyncAction } from 'typescript-fsa-redux-saga';

export function* init() {
  yield fx.all([
    fx.call(watchForUserLogIn),
    fx.call(watchForUserLogOut),
    fx.call(watchForUserSignUp),
    fx.call(watchForUserResetPassword),
    fx.call(watchForUserAuthenticationWithToken),
    fx.call(watchForUserProfileUpdate),
    fx.call(watchForTrackUserRegistration),
    fx.call(watchForSendConfirmationEmail),
    fx.call(watchForEmailVerification),
  ]);
}

type AuthenticationRequestAction = Action<IRequestUserAuthenticationActionPayload>;
type TokenAuthenticationRequestAction = Action<IRequestUserTokenAuthenticationActionPayload>;
type ResetPasswordRequestAction = Action<IRequestResetPasswordActionPayload>;
type LogOutRequestAction = Action<IRequestUserLogOutActionPayload>;
type UpdateProfileAction = Action<IRequestUpdateUserProfileActionPayload>;
type TrackUserRegistrationAction = Action<IRequestTrackUserRegistrationActionPayload>;
type SendConfirmationEmailAction = Action<IPromiseActionPayload>;
type VerifyEmailAction = Action<IRequestVerifyUserEmailActionPayload>;

const userSagaWorkerFactory = <P, R = IMutationResult>(
  action: AsyncActionCreators<P, R, any>,
  asynFunc: (params: P, ...args) => Promise<R>,
) =>
  bindAsyncAction<P, R>(action)(
    function* (params: P, ...args): SagaIterator {
      return yield fx.call(asynFunc, params, ...args);
    },
  );

const logInWorker = userSagaWorkerFactory<IUserCredentials, CognitoUserSession>(actions.logInUser, logInUser);
const signUpWorker = userSagaWorkerFactory<IUserCredentials, CognitoUser>(actions.signUpUser, signUpUser);
const logOutWorker = userSagaWorkerFactory<IUserLogOutActionPayload>(actions.logOutUser, logOutUser);
const authenticateWithTokenWorker = userSagaWorkerFactory<IUserTokenAuthenticationActionPayload, any>(
  actions.authenticateUserWithToken, authenticateWithToken);
const resetPasswordWorker = userSagaWorkerFactory<IResetPasswordActionPayload, void>(
  actions.resetPassword, resetPassword);
const updateProfileWorker =
  userSagaWorkerFactory<IUpdateUserProfileActionPayload, PayloadError[] | null | IMutationResult>(
  actions.updateUserProfile, updateProfile);
const trackUserRegistrationWorker =
  userSagaWorkerFactory<IUpdateUserProfileActionPayload, PayloadError[] | null | IMutationResult>(
  actions.trackNewUserRegistration, updateProfile);
const sendConfirmationEmailWorker = userSagaWorkerFactory(actions.sendConfirmationEmail, sendConfirmationEmail);
const verifyEmailWorker = userSagaWorkerFactory<IVerifyEmailActionPayload>(
  actions.verifyUserEmail, verifyUserEmail);

function* watchForUserLogIn() {
  while (true) {
    const { payload: {
      credentials,
      promise,
    } }: AuthenticationRequestAction = yield fx.take(actions.requestUserLogIn);

    try {
      // For now, we take for granted that the user pool is in the redux store
      const userPool: CognitoUserPool = yield fx.select(getUserPool)!;
      const session = yield fx.call(logInWorker, credentials, userPool);
      promise.resolve(session);
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForUserSignUp() {
  while (true) {
    const { payload: {
      credentials,
      promise,
    } }: AuthenticationRequestAction = yield fx.take(actions.requestUserSignUp);
    const userPool: CognitoUserPool = yield fx.select(getUserPool)!;

    try {
      yield fx.call(signUpWorker, credentials, userPool);
      // Auto-magically log in user right after sign up
      yield fx.put(actions.requestUserLogIn({ credentials, promise }));
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForSendConfirmationEmail() {
  while (true) {
    const { payload: {
      promise,
    } }: SendConfirmationEmailAction = yield fx.take(actions.requestSendConfirmationEmail);

    try {
      // For now, we take for granted that the user pool is in the redux store
      const userPool: CognitoUserPool = yield fx.select(getUserPool)!;
      yield fx.call(sendConfirmationEmailWorker, undefined, userPool);
      promise.resolve();
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForEmailVerification() {
  while (true) {
    const { payload: {
      code,
      email,
      promise,
    } }: VerifyEmailAction = yield fx.take(actions.requestVerifyUserEmail);

    try {
      // For now, we take for granted that the user pool is in the redux store
      const userPool: CognitoUserPool = yield fx.select(getUserPool)!;
      yield fx.call(verifyEmailWorker, { email, code }, userPool);
      promise.resolve();
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForUserAuthenticationWithToken() {
  while (true) {
    const { payload: {
      idToken,
      promise,
      viewerId,
    } }: TokenAuthenticationRequestAction = yield fx.take(actions.requestUserAuthenticationWithToken);

    try {
      const result = yield fx.call(authenticateWithTokenWorker, { idToken, viewerId });
      promise.resolve(result);
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForUserProfileUpdate() {
  while (true) {
    const { payload: {
      input,
      promise,
      viewerId,
    } }: UpdateProfileAction = yield fx.take(actions.requestUpdateUserProfile);

    try {
      const result = yield fx.call(updateProfileWorker, { input, viewerId });
      promise.resolve(result);
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForTrackUserRegistration() {
  while (true) {
    const { payload: {
      promise,
      viewerId,
    } }: TrackUserRegistrationAction = yield fx.take(actions.requestTrackUserRegistration);

    const input: GQL.IUpdateProfileInput = {
      ga_account_goal_sent: true,
    };

    try {
      const result = yield fx.call(trackUserRegistrationWorker, { input, viewerId });
      promise.resolve(result);
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForUserResetPassword() {
  while (true) {
    const { payload: {
      code,
      email,
      promise,
      password,
    } }: ResetPasswordRequestAction = yield fx.take(actions.requestResetPassword);

    try {
      const userPool: CognitoUserPool = yield fx.select(getUserPool)!;
      yield fx.call(resetPasswordWorker, { code, email, password }, userPool);
      promise.resolve();
    } catch (error) {
      promise.reject(error);
    }
  }
}

function* watchForUserLogOut() {
  while (true) {
    const { payload: {
      promise,
      viewerId,
    } }: LogOutRequestAction = yield fx.take(actions.requestUserLogOut);

    try {
      const result = yield fx.call(logOutWorker, { viewerId });
      yield fx.put(clearLocalStorage());
      promise.resolve(result);
    } catch (error) {
      promise.reject(error);
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/sagas/user.ts